import type { ComponentType, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import type { ISODate } from '@app/shared'

import type { SearchHit, SearchHitKind, SearchRelatedItem } from '../../lib/data'
import { dateLabel, KIND_LABEL, otherMatchesLine } from './labels'
import {
  ArrowRight,
  Calendar,
  Cap,
  CheckSquare,
  Document,
  type IconProps,
  Journal,
  JumpTo,
  Magnifier,
  Mic,
  RedditFace,
  Speech,
  Sparkle,
  VideoBox,
  XMark,
} from './icons'

// ============================================================================
// SEARCH · THE REPEATED PIECES — docs/design/handoff/"LifeOS Search.dc.html",
// options 10a and 10b.
//
// TWO ROW FAMILIES, and the difference between them is the whole screen:
//
//   ResultRow   a LITERAL match. White card, the item's own words, the query
//               highlighted IN PLACE, a domain badge, a date and a deep link.
//   RelatedRow  a SEMANTIC guess. Violet-cast card, one line, no snippet and
//               nothing highlighted — because there is nothing in it to
//               highlight. That is what "no literal match" means, and the
//               visual difference is the label doing its job before anyone
//               reads the divider above it.
//
// They are never interleaved (README §Interactions → Search). The data contract
// hands them over as two arrays so that no sort in here can mix them.
// ============================================================================

// ── The matched term, highlighted in place ──────────────────────────────────

const isWordChar = (ch: string) => /[a-z0-9]/.test(ch)

interface Span {
  from: number
  to: number
}

/**
 * Every word-start occurrence of any term, merged where they overlap.
 *
 * WORD STARTS, not `includes`, so the highlight is the same rule the index matched with. A
 * highlight that lands somewhere the search did not look is worse than no highlight: it teaches
 * the wrong thing about how the box works.
 */
function spansOf(text: string, terms: readonly string[]): Span[] {
  const hay = text.toLowerCase()
  const raw: Span[] = []
  for (const term of terms) {
    if (!term) continue
    let from = 0
    for (;;) {
      const at = hay.indexOf(term, from)
      if (at === -1) break
      if (at === 0 || !isWordChar(hay[at - 1]!)) {
        // THE WHOLE WORD, not the prefix that matched it. The index matches 'model' inside
        // 'models' on purpose, and highlighting only the first five letters renders as
        // "model s" — a word broken in half by its own highlight.
        let to = at + term.length
        while (to < hay.length && isWordChar(hay[to]!)) to++
        raw.push({ from: at, to })
      }
      from = at + 1
    }
  }
  raw.sort((a, b) => a.from - b.from || b.to - a.to)
  const merged: Span[] = []
  for (const span of raw) {
    const last = merged[merged.length - 1]
    // Overlapping spans merge, and so do two that are separated by nothing but ONE SPACE: a
    // phrase query is a phrase, and 'motion quality' drawn as two markers with a gap between
    // them reads as two coincidences instead of the thing that was asked for. The mockup draws
    // it as one marker; so does this.
    if (last && (span.from <= last.to || (span.from === last.to + 1 && text[last.to] === ' '))) {
      last.to = Math.max(last.to, span.to)
    } else {
      merged.push({ ...span })
    }
  }
  return merged
}

/** The mockup's marker — the `highlight` token, radius 3, 3px of side padding. */
export function Highlight({ text, terms }: { text: string; terms: readonly string[] }) {
  const spans = spansOf(text, terms)
  if (spans.length === 0) return <>{text}</>
  const out: ReactNode[] = []
  let at = 0
  spans.forEach((span, i) => {
    if (span.from > at) out.push(text.slice(at, span.from))
    out.push(
      <mark key={i} className="rounded-[3px] bg-highlight px-[3px] text-inherit">
        {text.slice(span.from, span.to)}
      </mark>,
    )
    at = span.to
  })
  if (at < text.length) out.push(text.slice(at))
  return <>{out}</>
}

// ── Kind → glyph, tile, badge ───────────────────────────────────────────────

interface KindStyle {
  Icon: ComponentType<IconProps>
  /** The 20px tile's tint. Dropped on a related row — see KindTile. */
  tileBg: string
  /** The glyph's own hue, which the tile keeps in both families. */
  glyph: string
  /** The DOMAIN · TYPE pill: ground and text. */
  badge: string
}

const KIND_STYLE: Record<SearchHitKind, KindStyle> = {
  journal_entry: { Icon: Journal, tileBg: 'bg-warning-fill', glyph: 'text-glyph-journal', badge: 'bg-warning-fill text-warning-2' },
  voice_memo: { Icon: Journal, tileBg: 'bg-warning-fill', glyph: 'text-glyph-journal', badge: 'bg-warning-fill text-warning-2' },
  transcript: { Icon: Mic, tileBg: 'bg-info-fill-2', glyph: 'text-source-web', badge: 'bg-info-fill text-info' },
  announcement: { Icon: Speech, tileBg: 'bg-info-fill', glyph: 'text-info', badge: 'bg-info-fill text-info' },
  syllabus: { Icon: Document, tileBg: 'bg-info-fill-2', glyph: 'text-source-web', badge: 'bg-info-fill text-info' },
  assignment: { Icon: Calendar, tileBg: 'bg-info-fill', glyph: 'text-info', badge: 'bg-info-fill text-info' },
  lecture_summary: { Icon: Document, tileBg: 'bg-info-fill-2', glyph: 'text-source-web', badge: 'bg-info-fill text-info' },
  // The same document glyph the syllabus row wears, because it is the same object seen one layer
  // down: `syllabus` is what a model made of the file, `course_file` is the file.
  course_file: { Icon: Document, tileBg: 'bg-info-fill-2', glyph: 'text-source-web', badge: 'bg-info-fill text-info' },
  save_reddit: { Icon: RedditFace, tileBg: 'bg-source-reddit-fill', glyph: 'text-orange-2', badge: 'bg-source-reddit-fill text-orange' },
  save_youtube: { Icon: VideoBox, tileBg: 'bg-danger-fill', glyph: 'text-danger-dot', badge: 'bg-danger-fill text-danger-2' },
  save_article: { Icon: Document, tileBg: 'bg-info-fill-2', glyph: 'text-source-web', badge: 'bg-info-fill-2 text-info-2' },
  save_x: { Icon: XMark, tileBg: 'bg-fill', glyph: 'text-muted', badge: 'bg-fill text-ink-3' },
  task: { Icon: CheckSquare, tileBg: 'bg-orange-fill-2', glyph: 'text-glyph-tasks', badge: 'bg-orange-fill-2 text-orange' },
  learning_item: { Icon: Cap, tileBg: 'bg-teal-fill', glyph: 'text-learning', badge: 'bg-teal-fill text-teal' },
  learning_session: { Icon: Cap, tileBg: 'bg-teal-fill', glyph: 'text-learning', badge: 'bg-teal-fill text-teal' },
}

function KindTile({ kind, related }: { kind: SearchHitKind; related?: boolean }) {
  const { Icon, tileBg, glyph } = KIND_STYLE[kind]
  return (
    <span
      className={clsx(
        'flex size-5 flex-none items-center justify-center rounded-keycap',
        // On a related row the tile drops its tint for a white square on the accent line: the
        // card ground is already carrying the "this is a guess" signal, and two tints fight.
        related ? 'border border-accent-line bg-surface' : tileBg,
        glyph,
      )}
    >
      <Icon size={12} />
    </span>
  )
}

function KindBadge({ kind }: { kind: SearchHitKind }) {
  return (
    <span
      className={clsx(
        'flex-none rounded-pill px-[7px] text-[9.5px] font-semibold leading-[1.6] tracking-[0.04em]',
        KIND_STYLE[kind].badge,
      )}
    >
      {KIND_LABEL[kind]}
    </span>
  )
}

/** The quiet grey pills a journal entry's tags and a save's note count ride in. */
function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="flex-none rounded-pill bg-fill-2 px-2 text-[10px] font-medium leading-[1.6] text-ink-3">
      {children}
    </span>
  )
}

// ── The literal result row ──────────────────────────────────────────────────

/**
 * One exact match. The whole row is the link, because a result you have to aim at is a result
 * that costs a second every time — and the focus ring lands on the card, which is where a
 * keyboard user needs to see it.
 *
 * A row with no body (a task, a deadline) collapses to ONE LINE, exactly as the mockup's task row
 * does. Nothing is padded out to a uniform height: a task is its title, and a second line of
 * whitespace under it would imply there is more to read.
 */
export function ResultRow({
  hit,
  terms,
  today,
}: {
  hit: SearchHit
  terms: readonly string[]
  today: ISODate
}) {
  const when = dateLabel(hit.kind, hit.date, today)
  const others = otherMatchesLine(hit.otherMatches, hit.kind)
  const inline = hit.snippet === null

  const meta: ReactNode[] = []
  if (hit.timestampMs !== null) {
    meta.push(
      <span key="jump" className="flex flex-none items-center gap-[5px] text-[10.5px] font-semibold leading-none text-success-dot">
        <JumpTo />
        Jump to {hit.locator.split(' · ').pop()}
      </span>,
    )
  }
  for (const chip of hit.chips) meta.push(<Chip key={`chip-${chip}`}>{chip}</Chip>)
  if (hit.note) {
    meta.push(
      <span key="note" className="min-w-0 truncate text-[10.5px] font-normal leading-none text-faintest-2">
        {hit.note}
      </span>,
    )
  }
  if (others) {
    meta.push(
      <span key="others" className="flex-none text-[10.5px] font-normal leading-none text-faintest-2">
        {others}
      </span>,
    )
  }

  return (
    <Link
      to={hit.href}
      className={clsx(
        'flex flex-none gap-[14px] rounded-panel border border-line bg-surface',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2',
        inline ? 'items-center px-[17px] py-[13px]' : 'px-[17px] py-[14px]',
      )}
    >
      <KindTile kind={hit.kind} />

      {inline ? (
        <>
          <span className="flex min-w-0 flex-1 items-center gap-[11px]">
            <KindBadge kind={hit.kind} />
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-[1.3]">
              <Highlight text={hit.title ?? hit.locator} terms={terms} />
            </span>
            <span className="flex flex-none items-center gap-[5px] rounded-pill bg-fill-2 px-[9px] py-[3px] text-[10.5px] font-medium leading-[1.5] text-ink-2">
              <Highlight text={hit.locator} terms={terms} />
            </span>
          </span>
          {when && (
            <span className="flex-none text-[10.5px] font-normal leading-none text-microcopy">{when}</span>
          )}
        </>
      ) : (
        <span className="flex min-w-0 flex-1 flex-col gap-[7px]">
          <span className="flex items-center gap-[9px]">
            <KindBadge kind={hit.kind} />
            <span className="min-w-0 truncate text-[12px] font-semibold leading-none text-ink-2">
              <Highlight text={hit.locator} terms={terms} />
            </span>
            {when && (
              <span className="ml-auto flex-none text-[10.5px] font-normal leading-none text-microcopy">
                {when}
              </span>
            )}
          </span>

          {hit.title !== null && (
            <span className="text-[13.5px] font-semibold leading-[1.4] text-pretty">
              <Highlight text={hit.title} terms={terms} />
            </span>
          )}

          <span
            className={clsx(
              'text-pretty',
              hit.title !== null
                ? 'text-[12px] font-normal leading-[1.55] text-ink-3'
                : 'text-[12.5px] font-normal leading-[1.6] text-ink-2',
            )}
          >
            <Highlight text={hit.snippet ?? ''} terms={terms} />
          </span>

          {meta.length > 0 && <span className="flex items-center gap-2">{meta}</span>}
        </span>
      )}
    </Link>
  )
}

// ── The related row ─────────────────────────────────────────────────────────

/** The divider the related block sits under. A hard rule, not a heading with more air above it. */
export function RelatedDivider() {
  return (
    <div className="flex flex-none items-center gap-3 pb-1 pt-[14px]">
      <span className="h-px flex-1 bg-dash" aria-hidden />
      <span className="flex items-center gap-2 rounded-pill border border-accent-line bg-accent-fill px-[13px] py-[5px]">
        <Sparkle className="text-chat" />
        <span className="text-[10.5px] font-semibold leading-none tracking-[0.06em] text-accent">
          RELATED · NO LITERAL MATCH
        </span>
      </span>
      <span className="h-px flex-1 bg-dash" aria-hidden />
    </div>
  )
}

export function RelatedRow({ item, today }: { item: SearchRelatedItem; today: ISODate }) {
  const when = dateLabel(item.kind, item.date, today)
  return (
    <Link
      to={item.href}
      className={clsx(
        'flex flex-none items-center gap-[14px] rounded-panel border border-accent-line bg-related-fill px-[17px] py-[13px]',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface',
      )}
    >
      <KindTile kind={item.kind} related />
      <span className="flex min-w-0 flex-1 items-center gap-[11px]">
        <KindBadge kind={item.kind} />
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium leading-[1.3]">
          {item.title}
        </span>
        {item.badge && <Chip>{item.badge}</Chip>}
      </span>
      {when && <span className="flex-none text-[10.5px] font-normal leading-none text-microcopy">{when}</span>}
    </Link>
  )
}

// ── Section headings ────────────────────────────────────────────────────────

export function BlockHeading({ label, note }: { label: string; note?: string }) {
  return (
    <div className="flex flex-none items-center gap-[9px] pb-[2px]">
      <span className="text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy">
        {label}
      </span>
      {note && (
        <span className="text-[10.5px] font-normal leading-none text-faintest-2">{note}</span>
      )}
    </div>
  )
}

// ── The two footers ─────────────────────────────────────────────────────────

/** 10a's dashed row: one way out of a search that did not quite land. */
export function AskChatRow({ tail }: { tail?: string }) {
  return (
    <div className="flex flex-none items-center gap-[11px] rounded-banner border border-dashed border-dash px-4 py-3">
      <Link
        to="/chat"
        className="flex items-center gap-[11px] rounded-chip text-[12px] font-medium leading-none text-ink-3"
      >
        <Speech size={15} strokeWidth={1.8} className="flex-none text-chat" />
        Not finding it? Ask in your own words
        <ArrowRight className="flex-none text-muted" />
      </Link>
      {tail && <span className="ml-auto text-[10.5px] font-normal leading-none text-faintest-2">{tail}</span>}
    </div>
  )
}

/**
 * 10b's dark bar. It appears only when every result IS a save, because "summarize these 7 saves"
 * is a false description of a list that also contains a lecture and a deadline.
 *
 * IT IS STILL INERT, AND THE REASON IS NOT A VERSION NUMBER. Chat shipped (V4) and this row's old
 * hover text still said it was waiting for it. Two things are actually missing, and both are
 * checkable rather than scheduled:
 *
 *   1. There is no result-set scope. `ChatScopeId` is five fixed scopes plus `course:<id>`
 *      (apps/web/src/lib/data/types.ts) and the Worker resolves nothing else
 *      (apps/worker/src/lib/chat.ts → `resolveScope`). A thread cannot be pointed at a list.
 *   2. Saves are not in the retrieval index at all — `SEARCHABLE` covers seven tables and `saves`
 *      is not one of them (apps/worker/src/lib/search.ts), so an Interests-scoped thread has
 *      nothing citable either. The Interests screen's ask bar says the same thing in full.
 *
 * Sending the owner into a thread that would answer from the wrong corpus, or from none, is the
 * small lie this screen exists to avoid — so the row stays inert and names both gaps.
 */
export function SummarizeRow({ count, subject }: { count: number; subject: string }) {
  return (
    <button
      type="button"
      aria-disabled="true"
      onClick={(e) => e.preventDefault()}
      title={
        'Two things are missing, and neither is a release. Chat threads are scoped to a whole ' +
        'domain — Journal, School, one course — and there is no scope for one result list. And ' +
        'your saves are not in the index chat retrieves from, so a thread over them would have ' +
        'nothing to cite.'
      }
      className="flex flex-none cursor-not-allowed items-center gap-[11px] rounded-banner bg-ink px-4 py-[13px] text-left"
    >
      <Speech size={15} strokeWidth={1.8} className="flex-none text-chat-lift" />
      <span className="min-w-0 truncate text-[12.5px] font-medium leading-none text-surface">
        Summarize what these {count} saves say about {subject}
      </span>
      <ArrowRight className="flex-none text-chat-lift" />
      <span className="ml-auto flex-none text-[10.5px] font-normal leading-none text-on-tile-quiet">
        {/* It used to promise "opens Chat scoped to these results", which is the one thing this
            control cannot do: there is no scope for a result list, and saves are not indexed for
            chat at all. A label that describes an action the button will not take is worse than
            a label that admits it. */}
        no chat scope covers one result list
      </span>
    </button>
  )
}

// ── Empty, absent and loading ───────────────────────────────────────────────

/**
 * The no-matches card, transcribed from 10b's specimen. The second line is the one true thing
 * this screen can say about its own limits: it looks for words, and the other surface looks for
 * meaning.
 */
export function NoMatches({
  query,
  suggestions,
  onSuggest,
}: {
  query: string | null
  suggestions: readonly string[]
  onSuggest: (q: string) => void
}) {
  return (
    <div className="flex flex-none flex-col items-center gap-[13px] rounded-card border border-line bg-surface px-6 py-[34px]">
      <Magnifier size={26} strokeWidth={1.6} className="flex-none text-faintest-2" />
      <p className="text-[14px] font-medium leading-[1.4] text-ink-2">
        {query === null ? 'Search everything at once' : `No matches for “${query}”`}
      </p>
      <p className="max-w-[420px] text-center text-[12px] font-normal leading-[1.5] text-muted text-pretty">
        Search looks for your words. Chat can look for your meaning — across journal entries,
        transcripts, and saves at once.
      </p>
      <Link
        to="/chat"
        className="mt-[2px] flex items-center gap-[9px] rounded-row bg-ink px-4 py-[10px] text-[12px] font-semibold leading-none text-surface"
      >
        <Speech size={14} strokeWidth={1.8} className="flex-none text-chat-lift" />
        Not finding it? Ask in your own words
        <ArrowRight className="flex-none text-chat-lift" />
      </Link>
      {suggestions.length > 0 && (
        <div className="mt-[3px] flex items-center gap-2">
          <span className="text-[10.5px] font-normal leading-none text-faintest-2">or try:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSuggest(s)}
              className="rounded-pill border border-dashed border-dash px-[10px] py-1 text-[11px] font-medium leading-none text-muted transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-ink-2"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * What the results area shows when the read threw. In remote mode every search throws
 * NotImplementedError until V4, and an empty list would say "you have nothing like that" — a
 * claim about the owner's life, and a false one.
 */
export function Unavailable({ error }: { error: unknown }) {
  const message =
    error instanceof Error
      ? error.message
      : 'The search could not run, and the reason did not come back with a sentence attached.'
  return (
    <div className="flex flex-none items-start gap-[9px] rounded-card border border-line bg-surface px-[17px] py-[15px]">
      <span className="mt-[2px] size-[5px] flex-none rounded-full bg-microcopy" aria-hidden />
      <p className="text-[11.5px] leading-[1.45] text-ink-3 text-pretty">{message}</p>
    </div>
  )
}

/**
 * Skeletons with the geometry of a result row, never a spinner (README §Loading). The widths
 * step down so a loading list reads as a list rather than as a stack of identical bars.
 */
export function ResultSkeleton({ rows = 5 }: { rows?: number }) {
  const widths = ['w-[62%]', 'w-[78%]', 'w-[54%]', 'w-[70%]', 'w-[46%]']
  return (
    <div className="flex flex-col gap-[9px]" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex animate-pulse gap-[14px] rounded-panel border border-line bg-surface px-[17px] py-[14px]"
        >
          <div className="size-5 flex-none rounded-keycap bg-fill" />
          <div className="flex min-w-0 flex-1 flex-col gap-[9px]">
            <div className="flex items-center gap-[9px]">
              <div className="h-[13px] w-[92px] flex-none rounded-pill bg-fill" />
              <div className="h-[9px] w-[130px] rounded-full bg-line-3" />
            </div>
            <div className="h-[9px] w-full rounded-full bg-line-3" />
            <div className={clsx('h-[9px] rounded-full bg-line-3', widths[i % widths.length])} />
          </div>
        </div>
      ))}
    </div>
  )
}
